import React from 'react';
import { PageSection, UI, SvgIcon, HTMLParser } from '@gik/ui';
import bemBlock from '@gik/core/utils/bemBlock';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { FormRenderProps } from 'react-final-form';
import { IRecipientAddressPromptFormValues, RecipientAddressPromptForm } from './RecipientAddressPromptForm';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import i18n from '@gik/i18n';

// imports for useCollectRecipientInfo
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import { InkindPageAPIModel, PatchInkindPageRequest } from '@gik/core/models/gik/InkindPage';
import { optimisticUpdate } from '@gik/core/api';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';

export interface IRecipientAddressPromptProps {
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  initialValues?: Partial<IRecipientAddressPromptFormValues>;
  onSuccess?: (values: IRecipientAddressPromptFormValuesWithSource) => void;
  onSkip?: (values: IRecipientAddressPromptFormValuesWithSource) => void;
}

export interface IRecipientAddressPromptFormValuesWithSource extends IRecipientAddressPromptFormValues {
  isDontAskAgain?: boolean;
  whichButtonIsPressed?: 'skip' | 'next';
}

export const recipientAddressPromptBlockName = 'recipient-address-prompt';

export function RecipientAddressPrompt({
  buttonsPortal,
  onSuccess,
  onSkip,
  initialValues,
}: IRecipientAddressPromptProps): React.ReactElement {
  const bem = bemBlock(recipientAddressPromptBlockName);
  const { t } = useTranslation();

  const [isDontAskAgain, setIsDontAskAgain] = React.useState<boolean>(false);

  function handleSubmit(values: IRecipientAddressPromptFormValues) {
    if (onSuccess) onSuccess({ ...values, whichButtonIsPressed: 'next' });
  }

  const handleWhyAddAddress = async () => {
    const infoContent = (
      <>
        <p className={bem('dialog-text')}>{t(translationKeys.whyAddAddressDescription1)}</p>
        <p className={bem('dialog-text')}>
          <HTMLParser rawHtml={t(translationKeys.whyAddAddressDescription2)} />
        </p>
      </>
    );
    await UI.info(infoContent, {
      title: t(translationKeys.whyAddAddressTitle),
      okText: t(translationKeys.whyAddAddressButton),
      className: bem('why-add-dialog'),
    });
  };

  const handleSkip = async () => {
    const confirmContent = <p className={bem('dialog-text')}>{t(translationKeys.skipDialogDescription)}</p>;

    const response = await UI.confirm(confirmContent, {
      title: t(translationKeys.skipDialogTitle),
      okText: t(translationKeys.skipDialogOkButton),
      cancelText: t(translationKeys.skipDialogCancelButton),
      okButtonProps: { variant: 'danger', size: 'base' },
      cancelButtonProps: { variant: 'default', size: 'base' },
      className: bem('skip-dialog'),
    });

    if (response) {
      if (onSkip)
        onSkip({
          // just forward initalValues, they won't be used
          ...(initialValues as IRecipientAddressPromptFormValues),
          isDontAskAgain,
          whichButtonIsPressed: 'skip',
        });
    }
  };

  return (
    <>
      <PageSection noPad variant="aqua" gradientDarkening className={bem('header')}>
        <span className={bem('title')}>{t(translationKeys.title)}</span>
        <SvgIcon className={bem('icon')} onClick={handleWhyAddAddress} name="InformationCircle" />
        <p className={bem('description')}>{t(translationKeys.description)}</p>
      </PageSection>
      <div className={bem('content')}>
        <RecipientAddressPromptForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          buttonsPortal={buttonsPortal}
          isRecipientFullNameRequired={true}
          onSkip={handleSkip}
          isDontAskAgain={isDontAskAgain}
          setIsDontAskAgain={setIsDontAskAgain}
        />
      </div>
    </>
  );
}

export const recipientAddressPromptModalBlockName = 'recipient-address-prompt-modal';

export function openRecipientAddressPrompt(
  initialValues?: Partial<IRecipientAddressPromptFormValues>
): Promise<IRecipientAddressPromptFormValuesWithSource> {
  const bem = bemBlock(recipientAddressPromptModalBlockName);
  const footerId = 'recipient-prompt-address-form-footer-id';

  return UI.dialog(
    ({ close }) => (
      <RecipientAddressPrompt
        onSuccess={close}
        onSkip={close}
        buttonsPortal={() => document.getElementById(footerId)}
        initialValues={initialValues}
      />
    ),
    {
      title: i18n.t(translationKeys.modalTitle),
      padding: false,
      closable: true,
      className: bem(),
      footer: <ModalButtonFooter id={footerId} />,
    }
  );
}

export function useCollectRecipientInfo(): () => Promise<boolean> {
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);

  const getRecipientInfoFromInkindPage = (inkindPage: InkindPageAPIModel) => {
    return {
      recipientFullName: inkindPage.recipientFullName,
      address1: inkindPage.address,
      address2: inkindPage.address2,
      postalCode: inkindPage.zip,
      city: inkindPage.city,
      state: inkindPage.stateCode,
    };
  };

  const collectRecipientInfo = async (): Promise<boolean> => {
    const localStorageKey = 'gik-user-already-prompted-for-recipient-info-page-ids';

    // abort, we don't have required data to proceed
    if (!inkindRouteId || !inkindPage) return false;

    const pageIdsArray = JSON.parse(localStorage.getItem(localStorageKey)) || [];
    const isAlreadyPrompted = pageIdsArray.find(page => page.inkindRouteId === inkindRouteId);

    // abort, user was already prompted before
    if (isAlreadyPrompted) return true;

    const isRecipientInfoComplete = isInkindPageRecipientInfoComplete(inkindPage);

    if (!isRecipientInfoComplete) {
      const initialValues = getRecipientInfoFromInkindPage(inkindPage);
      const recipientInfo = await openRecipientAddressPrompt(initialValues);

      // don't ask me again is checked and skipped
      if (recipientInfo && recipientInfo.whichButtonIsPressed === 'skip' && recipientInfo.isDontAskAgain === true) {
        pageIdsArray.push({ inkindRouteId });
        localStorage.setItem(localStorageKey, JSON.stringify(pageIdsArray));
      }

      // form is submitted
      if (recipientInfo && recipientInfo.whichButtonIsPressed === 'next') {
        const patchInkindPageData: PatchInkindPageRequest = {
          recipient: {
            type: inkindPage.recipientType,
            fullName: recipientInfo.recipientFullName,
            address1: recipientInfo.address1,
            address2: recipientInfo.address2,
            city: recipientInfo.city,
            postalCode: recipientInfo.postalCode,
            state: recipientInfo.state,
          },
        };

        const newInkindPage: InkindPageAPIModel = {
          ...inkindPage,
          recipientFullName: recipientInfo.recipientFullName,
          address: recipientInfo.address1,
          address2: recipientInfo.address2,
          city: recipientInfo.city,
          stateCode: recipientInfo.state,
          zip: recipientInfo.postalCode,
        };

        optimisticUpdate(newInkindPage, mutateInkindPage, () => patchInkind(inkindRouteId, patchInkindPageData));
      }
    }
    return true;
  };
  return collectRecipientInfo;
}
